// import uniq from 'lodash/uniq';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useSWRConfig } from 'swr';
import { useSnackbar } from 'notistack';
// hooks
import {
  addToCart,
  deleteFromCart,
  useGetCartProducts,
  useGetCurrentOrder,
} from 'src/api/customer';
// import { useLocalStorage, getStorage } from 'src/hooks/use-local-storage';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
// _mock
// types
// import { IAddressItem } from 'src/types/address';
// import { ICheckoutItem } from 'src/types/checkout';
import { ICartProduct, IAddToCart, ICurrentCartOrder } from 'src/types';
import { endpoints } from 'src/utils/axios';
import { AUTH_STORAGE_KEYS } from 'src/utils/storage-available';
import { useLocalStorage } from 'src/hooks/use-local-storage';
import { useLocales } from 'src/locales';
import { canActAsClient } from 'src/utils/check-user-is-admin';
import { actingWithoutClient } from 'src/sections/customer/catalog/view/acting-client-selection';
import { useActingClientOptional } from 'src/sections/customer/acting-client';
import analytics, { ORDER_FLOW_STARTED_AT_KEY } from 'src/utils/analytics';
import { CHECKOUT_TABS, PRODUCT_CHECKOUT_STEPS } from '../common/constant';
//
import { CheckoutContext } from './checkout-context';

// ----------------------------------------------------------------------

// const STORAGE_KEY = 'checkout';

const initialState = {
  activeStep: CHECKOUT_TABS.ORDER,
  cartProducts: [],
  subTotal: 0,
  total: 0,
  discount: 0,
  shipping: 0,
  billing: null,
  totalItems: 0,
  discountPercentage: 0,
};

type Props = {
  children: React.ReactNode;
};

export function CheckoutProvider({ children }: Props) {
  const router = useRouter();
  const { mutate } = useSWRConfig();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useLocales();

  const [state, setState] = useState(initialState);
  const [confirmedOrder, setConfirmedOrder] = useState<ICurrentCartOrder | null>(null);
  const [placedOrderCode, setPlacedOrderCode] = useState<string>('');

  const updateState = useCallback((newState: any) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  }, []);

  const { state: storage, update: updateLegacySelection } = useLocalStorage(
    AUTH_STORAGE_KEYS.selectedAgentClientId,
    {},
  );
  // NAT-2497: one owner of the selection, for every role. Inside the
  // provider, `selectedAgentClientId` IS the acting venue — a customer's
  // chosen (or sole) venue as much as an agent's picked client — so every
  // cart, checkout and catalogue call that already sends it sends the right
  // one. Outside the provider (a test, a public page) the legacy storage key
  // still answers.
  const acting = useActingClientOptional();
  const selectedAgentClientId: string | number | undefined = acting
    ? (acting.actingClientId ?? undefined)
    : storage.selectedAgentClientId;
  const updateSelectedAgentClientId = useCallback(
    (name: string, value: string | number | undefined) => {
      if (acting) acting.setActingClientId(value == null || value === '' ? null : String(value));
      else updateLegacySelection(name, value);
    },
    [acting, updateLegacySelection],
  );

  // NAT-2497: inside the provider a customer with no venue known yet (grants
  // loading, none, or several unnamed) issues no client-scoped request — the
  // gate holds the page, and on the gate-less payment route this is what
  // keeps an id-less cart read from being refused for nothing.
  const venueKnown = !acting || acting.role !== 'customer' || acting.actingClientId != null;
  const { data: cart_products } = useGetCartProducts({
    clientId: selectedAgentClientId,
    enabled: venueKnown,
  });
  const { data: current_order } = useGetCurrentOrder({
    isCartEmpty: !cart_products || (cart_products && Object.keys(cart_products).length === 0),
    clientId: selectedAgentClientId,
    enabled: venueKnown,
  });

  // NAT-2497: a switch of the acting venue restarts the checkout. Clearing
  // the lines is not enough: with the address step still mounted, its form
  // keeps the previous venue's address and delivery fields and "next" would
  // submit them under the new venue's id, overwriting that order's
  // destination. The confirmed-order snapshot is that venue's too.
  const previousActingRef = useRef<string | number | undefined | null>(null);
  // Bumped on every switch. A checkout request already in flight was started
  // for the PREVIOUS venue: resetting the step does not cancel it, and its
  // continuation would otherwise advance or complete the new venue's checkout
  // with the old venue's answer. Callers take a token before they await and
  // drop their result when it comes back stale.
  const venueGenerationRef = useRef(0);
  useEffect(() => {
    const current = selectedAgentClientId ?? undefined;
    if (previousActingRef.current !== null && previousActingRef.current !== current) {
      venueGenerationRef.current += 1;
      updateState({ activeStep: CHECKOUT_TABS.ORDER });
      setConfirmedOrder(null);
      setPlacedOrderCode('');
    }
    previousActingRef.current = current;
  }, [selectedAgentClientId, updateState]);

  const beginVenueScopedOp = useCallback(() => {
    const startedAt = venueGenerationRef.current;
    return () => venueGenerationRef.current === startedAt;
  }, []);

  const refetchCart = useCallback(() => {
    mutate([
      endpoints.customer.cart_products.root,
      { params: { clientId: selectedAgentClientId } },
    ]);
    mutate([
      endpoints.customer.client_orders.current_order,
      { params: { clientId: selectedAgentClientId } },
    ]);
  }, [mutate, selectedAgentClientId]);

  /**
   * NAT-2494 — refuse a write from an acting role that has no client selected,
   * and say so. See `actingWithoutClient` for why: the server resolves an
   * absent id to the admin's OWN venue.
   */
  const refuseWithoutClient = useCallback((): boolean => {
    if (
      !actingWithoutClient({ canActAsClient: canActAsClient(), selected: selectedAgentClientId })
    ) {
      return false;
    }
    enqueueSnackbar(t('acting_client_required'), { variant: 'error' });
    return true;
  }, [selectedAgentClientId, enqueueSnackbar, t]);

  const onAddToCart = useCallback(
    async (
      data: IAddToCart,
      callback?: () => void,
      productMeta?: { name?: string; producer_name?: string; unit_price?: number },
    ) => {
      if (refuseWithoutClient()) {
        if (callback) callback();
        return;
      }
      try {
        await addToCart(data);
        refetchCart();
        if (!sessionStorage.getItem(ORDER_FLOW_STARTED_AT_KEY)) {
          sessionStorage.setItem(ORDER_FLOW_STARTED_AT_KEY, String(Date.now()));
        }
        if (callback) {
          callback();
        }
        // Tracked after the callback so a throw out of the SDK cannot swallow
        // the caller's continuation.
        analytics.track('product_added_to_order', {
          product_id: data.sku,
          product_name: productMeta?.name,
          producer_name: productMeta?.producer_name,
          quantity: data.quantity,
          unit_price: productMeta?.unit_price,
          ...(selectedAgentClientId ? { on_behalf_of_client: selectedAgentClientId } : {}),
        });
      } catch (error) {
        if (error?.response?.data) {
          enqueueSnackbar(error?.response?.data?.message, { variant: 'error' });
        }
        if (callback) {
          callback();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAgentClientId, refetchCart, refuseWithoutClient],
  );

  const onDeleteFromCart = useCallback(
    async (sku: string) => {
      if (refuseWithoutClient()) return;
      const existing = cart_products?.[sku];
      try {
        await deleteFromCart(sku, selectedAgentClientId);
        refetchCart();
        analytics.track('product_removed_from_order', {
          product_id: sku,
          product_name: existing?.product?.name,
          ...(selectedAgentClientId ? { on_behalf_of_client: selectedAgentClientId } : {}),
        });
      } catch (error) {
        if (error?.response?.data) {
          enqueueSnackbar(error?.response?.data?.message, { variant: 'error' });
        }
      }
    },
    [cart_products, selectedAgentClientId, refetchCart, enqueueSnackbar, refuseWithoutClient],
  );

  const getCartItem = useCallback(
    (sku: string) => (cart_products ? cart_products[sku] : undefined),
    [cart_products],
  );

  const onBackStep = useCallback(() => {
    updateState({ activeStep: state.activeStep - 1 });
  }, [updateState, state.activeStep]);

  const onNextStep = useCallback(() => {
    updateState({ activeStep: state.activeStep + 1 });
  }, [updateState, state.activeStep]);

  const onGotoStep = useCallback(
    (step: number) => {
      updateState({ activeStep: step });
    },
    [updateState],
  );

  const onApplyDiscount = useCallback((discount: number) => {
    // update('discount', discount);
  }, []);

  // const onApplyShipping = useCallback(
  //   (shipping: number) => {
  //     update('shipping', shipping);
  //   },
  //   [update],
  // );

  const completed = state.activeStep === PRODUCT_CHECKOUT_STEPS.length;

  // Reset
  const onReset = useCallback(() => {
    if (completed) {
      refetchCart();
      updateState({ activeStep: CHECKOUT_TABS.ORDER });
      router.replace(paths.customer.catalog);
    }
  }, [completed, refetchCart, updateState, router]);

  useEffect(() => {
    if (!cart_products) {
      // NAT-2494: an acting role with no client has no cart to show. The fetch
      // above resolves to no endpoint in that state, so `cart_products` stays
      // undefined and — without this — the LAST client's items stayed on
      // screen: the popover listed them, "place order" and "next" stayed
      // enabled, and a tap sent `clientId: ''`, which the server resolves to
      // the admin's OWN venue. Clearing the state is what disables those
      // buttons; `refuseWithoutClient` covers the popover's stepper.
      // NAT-2497: for EVERY role. A customer switching venue changes the SWR
      // key, `cart_products` is undefined while the new venue's cart loads,
      // and the previous venue's lines must not stay on screen writable under
      // the new venue's id.
      updateState({ cartProducts: [], totalItems: 0, total: 0, subTotal: 0 });
      return;
    }

    const { total_end_price, subTotal, modifiedCart } = Object.values(cart_products).reduce<{
      total_end_price: number;
      subTotal: number;
      modifiedCart: ICartProduct[];
    }>(
      (accumulator, cartProduct) => {
        accumulator.total_end_price += cartProduct.total_end_price;
        accumulator.subTotal += cartProduct.base_price * cartProduct.quantity;

        accumulator.modifiedCart.push(cartProduct);

        return accumulator;
      },
      { total_end_price: 0, subTotal: 0, modifiedCart: [] },
    );

    const discountValue = total_end_price - subTotal;

    updateState({
      cartProducts: modifiedCart,
      total: total_end_price,
      totalItems: modifiedCart.length || 0,
      subTotal,
      discount: discountValue,
      discountPercentage: (discountValue / subTotal) * 100 || 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart_products]);
  const memoizedValue = useMemo(
    () => ({
      ...state,

      completed,
      updateState,
      onReset,

      //
      //
      onAddToCart,
      onDeleteFromCart,
      getCartItem,
      refetchCart,
      // onDeleteCart,
      //
      // onIncreaseQuantity,
      // onDecreaseQuantity,
      //
      // onCreateBilling,
      onApplyDiscount,
      // onApplyShipping,
      //
      onBackStep,
      onNextStep,
      onGotoStep,
      //
      // onReset,
      current_order,
      confirmedOrder,
      setConfirmedOrder,
      placedOrderCode,
      setPlacedOrderCode,
      selectedAgentClientId,
      updateSelectedAgentClientId,
      beginVenueScopedOp,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      completed,
      onAddToCart,
      onDeleteFromCart,
      getCartItem,
      refetchCart,
      //
      //
      onApplyDiscount,
      // onApplyShipping,
      onBackStep,
      // onCreateBilling,
      // onDecreaseQuantity,
      // onDeleteCart,
      onGotoStep,
      // onIncreaseQuantity,
      onNextStep,
      // onReset,
      state,
      updateState,
      onReset,
      current_order,
      confirmedOrder,
      setConfirmedOrder,
      placedOrderCode,
      setPlacedOrderCode,
      selectedAgentClientId,
      updateSelectedAgentClientId,
    ],
  );

  return <CheckoutContext.Provider value={memoizedValue}>{children}</CheckoutContext.Provider>;
}
